import DataTable from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetComplianceData } from "@/hooks/use-postmaster";
import { type ColDef } from "ag-grid-community";
import { format } from "date-fns";
import { AlertCircle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function ComplianceTabContent({
  date,
  domain,
}: {
  date: string;
  domain: string;
}) {
  // Compliance API supports optional dates - if not provided, returns latest data
  const { startDate, endDate } = useMemo<{
    startDate: Date | undefined;
    endDate: Date | undefined;
  }>(() => {
    // For compliance, dates are optional - we can pass undefined to get latest data
    // But if a date range is selected, we'll use it
    if (!date || date === "latest") {
      return { startDate: undefined, endDate: undefined };
    }

    const end = new Date();
    const start = new Date();

    switch (date) {
      case "last7days":
        start.setDate(end.getDate() - 7);
        break;
      case "last30days":
        start.setDate(end.getDate() - 30);
        break;
      case "last60days":
        start.setDate(end.getDate() - 60);
        break;
      case "last90days":
        start.setDate(end.getDate() - 90);
        break;
      case "last120days":
        start.setDate(end.getDate() - 120);
        break;
      default:
        return { startDate: undefined, endDate: undefined };
    }

    return { startDate: start, endDate: end };
  }, [date]);

  const queryParams: {
    domain: string;
    startDate?: Date;
    endDate?: Date;
  } = {
    domain,
  };

  if (startDate && endDate) {
    queryParams.startDate = startDate;
    queryParams.endDate = endDate;
  }

  const { data, isLoading } = useGetComplianceData(queryParams);

  const getStatusBadge = (status: string) => {
    if (status === "Compliant") {
      return (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-400"
        >
          <CheckCircle2 className="h-3 w-3 mr-1" />
          {status}
        </Badge>
      );
    } else {
      return (
        <Badge
          variant="outline"
          className="bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-400"
        >
          <XCircle className="h-3 w-3 mr-1" />
          {status}
        </Badge>
      );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Fully Compliant":
        return "text-green-600";
      case "Mostly Compliant":
        return "text-blue-600";
      case "Partially Compliant":
        return "text-yellow-600";
      case "Non-Compliant":
      default:
        return "text-red-600";
    }
  };

  const columnDefs: ColDef[] = [
    {
      field: "requirement",
      headerName: "Requirement",
      filter: true,
      sortable: true,
      flex: 2,
    },
    {
      field: "status",
      headerName: "Status",
      filter: true,
      sortable: true,
      flex: 1,
      cellRenderer: (params: any) => {
        return getStatusBadge(params.value);
      },
    },
    {
      field: "isCompliant",
      headerName: "Compliant",
      filter: true,
      sortable: true,
      flex: 1,
      cellRenderer: (params: any) => {
        return params.value ? (
          <CheckCircle2 className="h-4 w-4 text-green-600" />
        ) : (
          <XCircle className="h-4 w-4 text-red-600" />
        );
      },
    },
  ];

  const formattedChartData = data?.trendData.map((item) => ({
    ...item,
    date: format(new Date(item.date), "MMM dd, yyyy"),
  }));

  const gridRowData = useMemo(() => {
    const base = data?.complianceData ?? [];
    const verdicts: { requirement: string; status: string; isCompliant: boolean }[] = [];
    if (data?.oneClickUnsubscribeVerdict) {
      const v = data.oneClickUnsubscribeVerdict;
      verdicts.push({
        requirement: "One-click unsubscribe",
        status: v.status,
        isCompliant: v.status === "Compliant",
      });
    }
    if (data?.honorUnsubscribeVerdict) {
      const v = data.honorUnsubscribeVerdict;
      verdicts.push({
        requirement: "Honour unsubscribe",
        status: v.status,
        isCompliant: v.status === "Compliant",
      });
    }
    return [...base, ...verdicts];
  }, [data?.complianceData, data?.oneClickUnsubscribeVerdict, data?.honorUnsubscribeVerdict]);

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-[50vh] w-full text-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-5">
        <Card className="shadow-none">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Requirements
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold">
              {data?.statistics.totalRequirements || 0}
            </div>
            <p className="text-xs text-muted-foreground">Requirements</p>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">Compliant</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold text-green-600">
              {data?.statistics.compliantCount || 0}
            </div>
            <p className="text-xs text-muted-foreground">Requirements</p>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">Non-Compliant</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold text-red-600">
              {data?.statistics.nonCompliantCount || 0}
            </div>
            <p className="text-xs text-muted-foreground">Requirements</p>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Compliance Percentage
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold">
              {data?.statistics.compliancePercentage.toFixed(1) || 0}%
            </div>
            <p className="text-xs text-muted-foreground">Overall</p>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div
              className={`text-sm font-medium ${getStatusColor(
                data?.statistics.status || ""
              )}`}
            >
              {data?.statistics.status || "Unknown"}
            </div>
            <p className="text-xs text-muted-foreground">Overall status</p>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-none">
        <CardHeader className="p-4">
          <CardTitle>Compliance Trend</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={formattedChartData}
                margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
              >
                <defs>
                  <linearGradient
                    id="complianceGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#e5e7eb"
                />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  tickMargin={10}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--background))",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                  formatter={(value: number) => [
                    `${value.toFixed(1)}%`,
                    "Compliance",
                  ]}
                  labelFormatter={(label) => label}
                />
                <Area
                  type="monotone"
                  dataKey="compliancePercentage"
                  stroke="#10B981"
                  strokeWidth={2}
                  fill="url(#complianceGradient)"
                  dot={{ stroke: "#10B981", strokeWidth: 2, r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="mt-6">
        <Card className="shadow-none">
          <CardHeader className="p-4">
            <CardTitle>Compliance Requirements</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <DataTable
              rowData={gridRowData}
              columnDefs={columnDefs}
              height={400}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Export the component for use in other files
export { ComplianceTabContent };

